import { transcriptSpeakerService } from '@src/services/transcript/TranscriptSpeakerService';
import {
  getElementMetaObject,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';

import type {
  ControlSetting,
  SpeakerControlSetting,
} from '@src/components/sidepanel/CompositionSettings/ControlTypes';
import type { Story } from '@src/types.ts/story';

// Minimal shape we rely on from editorStore to avoid tight coupling.
interface EditorStoreLike {
  editor: {
    timeline: { getElement: (id: string) => unknown };
    commandUpdateElement: (
      id: string,
      props: Record<string, unknown>,
    ) => boolean;
  };
}

type ConvertToVhFn = (val: string) => unknown;

const logger = LogManager.getLogger('utility:compositionSettings');

function maybeConvertFontSize(
  property: string,
  value: unknown,
  convertToVh?: ConvertToVhFn,
): Record<string, unknown> {
  if (property === 'font_size' && typeof value === 'number' && convertToVh) {
    return { font_size: convertToVh(String(value)) };
  }
  const obj: Record<string, unknown> = {};
  obj[property] = value;
  return obj;
}

interface ElementLike {
  id?: string;
  elements?: ElementLike[];
  [k: string]: unknown; // allow dynamic property assignment for control defaults
}

// Shared organization shape for branding/logo helpers
type OrganizationContext =
  | {
      branding?: {
        primaryColors?: unknown;
        secondaryColors?: unknown;
        brandFonts?: Array<{ fontFamily?: string } | Record<string, unknown>>;
      };
      organizationLogos?: { url: string }[];
    }
  | null
  | undefined;

function hasStringId(val: unknown): val is { id: string } {
  if (!val || typeof val !== 'object' || val === null) return false;
  // Use any intermediary to avoid unsafe assertions while meeting lint rule.
  const anyObj: any = val; // eslint-disable-line @typescript-eslint/no-explicit-any
  const idValue = anyObj.id;
  return typeof idValue === 'string';
}

function isElementLike(val: unknown): val is ElementLike {
  if (!val || typeof val !== 'object' || val === null) return false;
  if ('elements' in val) {
    const anyObj: any = val; // eslint-disable-line @typescript-eslint/no-explicit-any
    const elsUnknown = anyObj.elements;
    if (elsUnknown !== undefined && !Array.isArray(elsUnknown)) return false;
  }
  return true;
}

export function resolveElementFromPath(
  root: ElementLike,
  path: string[],
): ElementLike | undefined {
  let current: ElementLike | undefined = root;
  for (const seg of path) {
    if (!current) return undefined;
    const children: ElementLike[] | undefined = Array.isArray(current.elements)
      ? current.elements.filter(isElementLike)
      : undefined;
    if (!children) return undefined;
    const idx = Number(seg);
    if (Number.isNaN(idx) || idx < 0 || idx >= children.length)
      return undefined;
    const nextCandidate: unknown = children[idx];
    current = isElementLike(nextCandidate) ? nextCandidate : undefined;
  }
  return current;
}

function resolveChildElementId(
  root: ElementLike,
  path: string[],
): string | undefined {
  const el = resolveElementFromPath(root, path);
  return el?.id;
}

export function modifyCompositionProperty(
  elementId: string,
  editorStore: EditorStoreLike,
  elementPaths: string[][],
  property: string,
  value: unknown,
  convertToVh?: ConvertToVhFn,
): void {
  if (!elementId) {
    logger.warn('No element ID provided for modifyCompositionProperty', {
      elementId,
    });
    return;
  }
  // Look up root element from timeline by id
  // (we avoid storing a direct reference to keep this decoupled and testable)
  // eslint-disable-next-line no-restricted-syntax
  const rootRaw = editorStore.editor.timeline.getElement(elementId);

  if (!rootRaw || typeof rootRaw !== 'object' || !('id' in rootRaw)) {
    logger.warn('Active element not found for modifyCompositionProperty', {
      id: elementId,
    });
    return;
  }
  if (!hasStringId(rootRaw)) {
    logger.warn('Root element failed structural refinement');
    return;
  }
  if (!isElementLike(rootRaw)) {
    logger.warn('Root element failed element-like refinement');
    return;
  }
  const root: ElementLike = rootRaw;
  const targetIds = elementPaths
    .map(p => resolveChildElementId(root, p))
    .filter((id): id is string => typeof id === 'string');

  if (targetIds.length === 0) {
    logger.warn('No valid target ids for modifyCompositionProperty', {
      elementPaths,
    });
    return;
  }

  const payload = maybeConvertFontSize(property, value, convertToVh);

  targetIds.forEach(id => {
    const ok = editorStore.editor.commandUpdateElement(id, payload);
    if (!ok) {
      logger.warn('Failed to update composition element property', {
        property,
        targetId: id,
      });
    }
  });
}

export function getDefaultVideoValue(
  control: ControlSetting,
  story?: Story,
): unknown {
  if (control.type !== 'video') return undefined;
  const videoUrlCandidate = story?.storyArtifactsVideo?.[0]?.url;
  return typeof videoUrlCandidate === 'string' && videoUrlCandidate.length > 0
    ? videoUrlCandidate
    : undefined;
}
/**
 * Resolve default brand logo image URL for an image control whose id === 'logo'.
 */
export function getDefaultImageOrLogoValue(
  control: ControlSetting,
  organization?: OrganizationContext,
): unknown {
  if (control.type !== 'image' && control.type !== 'logo') return undefined;
  const logoUrlCandidate = organization?.organizationLogos?.[0]?.url;
  return typeof logoUrlCandidate === 'string' && logoUrlCandidate.length > 0
    ? logoUrlCandidate
    : undefined;
}

/**
 * Resolve default primary/secondary brand color for a color control whose brandKit matches.
 * Accepts comma-delimited string or array; returns first non-empty trimmed token.
 */
export function getDefaultColorValue(
  control: ControlSetting,
  organization?: OrganizationContext,
): unknown {
  const def = control.default;
  if (!def) return undefined;
  if (control.type !== 'color') return undefined;
  const brandKey = def.brandKit;
  if (brandKey !== 'primaryColor' && brandKey !== 'secondaryColor')
    return undefined;
  const branding = organization?.branding;
  if (!branding) return undefined;
  const raw =
    brandKey === 'primaryColor'
      ? branding.primaryColors
      : branding.secondaryColors;
  const extractFirst = (val: unknown): string | undefined => {
    if (typeof val === 'string') {
      const token = val
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)[0];
      return token || undefined;
    }
    if (Array.isArray(val)) {
      const first = val
        .map(v => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean)[0];
      return first || undefined;
    }
    return undefined;
  };
  return extractFirst(raw);
}

/**
 * Resolve default brand font family for a font control with brandKit === 'defaultFont'.
 */
export function getDefaultFontValue(
  control: ControlSetting,
  organization?: OrganizationContext,
): unknown {
  const def = control.default;
  if (!def) return undefined;
  if (control.type !== 'font' || def.brandKit !== 'defaultFont')
    return undefined;
  const fonts = Array.isArray(organization?.branding?.brandFonts)
    ? organization?.branding?.brandFonts
    : undefined;
  if (!fonts || fonts.length === 0) return undefined;
  const first = fonts[0];
  const hasFontFamily = (val: unknown): val is { fontFamily: unknown } =>
    !!val && typeof val === 'object' && val !== null && 'fontFamily' in val;
  if (hasFontFamily(first)) {
    const raw = first.fontFamily;
    if (typeof raw === 'string') return raw;
  }
  return undefined;
}

/**
 * Dispatcher to obtain a specialized default value for a control (logo/color/font) or fall back to control.default.value.
 */
export function getDefaultControlValue(
  control: ControlSetting,
  organization?: OrganizationContext,
  story?: Story,
): unknown {
  const videoVal = getDefaultVideoValue(control, story);
  if (videoVal !== undefined) return videoVal;
  const imageVal = getDefaultImageOrLogoValue(control, organization);
  if (imageVal !== undefined) return imageVal;
  const colorVal = getDefaultColorValue(control, organization);
  if (colorVal !== undefined) return colorVal;
  const fontVal = getDefaultFontValue(control, organization);
  if (fontVal !== undefined) return fontVal;
  return control.default?.value;
}

/**
 * Apply default control values into an in-memory composition source object before element creation.
 * Traverses elementPaths similarly to modifyCompositionProperty, but mutates the provided source tree object.
 * Only applies when control.default.value is defined (not null/undefined) and elementPaths resolve to valid child ids.
 */
export function applyDefaultControlValuesToSource({
  source,
  controls,
  organization,
  story,
  convertToVh,
}: {
  source: Record<string, unknown>;
  controls: ControlSetting[] | undefined | null;
  organization?: OrganizationContext;
  story?: Story;
  convertToVh?: ConvertToVhFn;
}): Record<string, unknown> {
  if (!source || typeof source !== 'object') return source;
  if (!Array.isArray(controls) || controls.length === 0) return source;

  const rootElement: ElementLike = isElementLike(source)
    ? source
    : { id: 'root' };

  for (const control of controls) {
    if (!control || typeof control !== 'object') continue;
    if (typeof control.property !== 'string') continue;

    const value = getDefaultControlValue(control, organization, story);
    if (value === null || typeof value === 'undefined') continue;
    if (
      !Array.isArray(control.elementPaths) ||
      control.elementPaths.length === 0
    )
      continue;
    if (!rootElement) continue;
    for (const path of control.elementPaths) {
      if (!Array.isArray(path) || path.length === 0) continue;
      const targetEl = resolveElementFromPath(rootElement, path);
      if (!targetEl) continue;
      const payload = maybeConvertFontSize(
        control.property,
        value,
        convertToVh,
      );
      for (const [k, v] of Object.entries(payload)) {
        Reflect.set(targetEl, k, v);
      }
    }
  }
  return source;
}

/**
 * Applies a mapping of meta control identifiers to input value keys.
 * Example:
 *  controlBindings = { "headlineText": "title", "accentColor": "brandColor" }
 *  inputValuesObject = { title: "Breaking News", brandColor: "#FF0000" }
 * Will find controls whose control.id matches 'headlineText' & 'accentColor'
 * and call modifyCompositionProperty for each with the mapped value.
 */
export function applySpeakerControlValueMappingToControls(
  elementId: string,
  editorStore: EditorStoreLike,
  controls: ControlSetting[],
  controlBindings: Record<string, string>,
  inputValuesObject: { [key: string]: unknown | null }, // Accept any keyed object of values (may include nulls)
  convertToVh?: ConvertToVhFn,
): void {
  if (!elementId) return;
  if (!controls || controls.length === 0) return;
  const pairs = Object.entries(controlBindings);
  if (pairs.length === 0) return;

  const controlIndex: Record<string, ControlSetting> = {};
  for (const c of controls) {
    if (c.id) controlIndex[c.id] = c;
  }

  for (const [controlId, valueKey] of pairs) {
    const control = controlIndex[controlId];
    if (!control) continue; // unmapped control id
    if (!(valueKey in inputValuesObject)) continue; // value key absent
    const value = inputValuesObject[valueKey];
    if (value === null || typeof value === 'undefined') continue; // skip null/undefined values
    modifyCompositionProperty(
      elementId,
      editorStore,
      control.elementPaths,
      control.property,
      value,
      convertToVh,
    );
  }
}

/**
 * Resolve speaker at a given timeline time and apply its mapped values to a composition's speaker control.
 * Shared between initial composition creation and later element movements (speaker should reflect new position).
 */
export function getCompositionControlsForElement(opts: {
  videoCreator: { currentVideo?: { extraElementData?: Record<string, any> } };
  elementId: string;
}): ControlSetting[] {
  const { videoCreator, elementId } = opts;
  const meta = getElementMetaObject(videoCreator.currentVideo, elementId);
  if (meta && typeof meta === 'object') {
    const raw = (meta as Record<string, unknown>).compositionControls;
    if (Array.isArray(raw)) {
      // Best-effort structural refinement: ensure objects with at least a property string and elementPaths array
      return raw.filter(r => r && typeof r === 'object') as ControlSetting[];
    }
  }
  return [];
}

export async function resolveAndApplySpeakerForCompositionElement(params: {
  elementId: string;
  editorStore: EditorStoreLike;
  datoClientStore: any; // minimal duck type for service
  videoCreator: {
    storyId?: string;
    time: number;
    rootStore: { transcriptionStore?: any };
    currentVideo?: { extraElementData?: Record<string, any> };
  };
  timelineTime?: number; // optional override for timeSeconds
}): Promise<boolean> {
  const {
    elementId,
    editorStore,
    datoClientStore,
    videoCreator,
    timelineTime,
  } = params;
  if (!elementId) return false;

  const compositionControls = getCompositionControlsForElement({
    videoCreator,
    elementId,
  });
  if (!Array.isArray(compositionControls) || compositionControls.length === 0)
    return false;

  const speakerControl = compositionControls.find(
    (c): c is SpeakerControlSetting => c.type === 'speaker',
  );
  if (!speakerControl) return false; // nothing to do

  try {
    const result = await transcriptSpeakerService.resolveSpeakerAtTime({
      storyId: videoCreator.storyId,
      timeSeconds:
        typeof timelineTime === 'number' ? timelineTime : videoCreator.time,
      getTranscriptElements: () =>
        videoCreator.rootStore.transcriptionStore?.getFinalTranscriptionElements?.(),
      datoClientStore,
    });

    if (result?.speaker) {
      const speakerPlain: Record<string, unknown> = {};
      if (result.speaker && typeof result.speaker === 'object') {
        for (const [k, v] of Object.entries(result.speaker)) {
          speakerPlain[k] = v;
        }
      }
      applySpeakerControlValueMappingToControls(
        elementId,
        editorStore,
        compositionControls,
        speakerControl.controlBindings || {},
        speakerPlain,
      );
      // Persist the resolved speaker label (id) onto extraElementData meta for later retrieval
      if (videoCreator.currentVideo) {
        const speakerId = speakerPlain.id;
        if (typeof speakerId === 'string' && speakerId.trim()) {
          upsertElementMetaObject(videoCreator.currentVideo, elementId, {
            speakerLabel: speakerId,
          });
        }
      }
      return true;
    }
  } catch (error) {
    logger.warn('Failed to resolve/apply speaker for composition element', {
      elementId,
      error,
    });
  }
  return false;
}
